import { api } from "./client";
import { apiRoutes } from "util/apiRoutes";
import { Comment } from "util/constants";
import type { PrivacyLevel } from "util/userUtils";

/**
 * API client for principle / task context comments.
 *
 * Mirrors the backend at `/api/posts/:postId/principles|tasks` + the
 * purpose-built `/api/comments/search` endpoint used by the picker.
 * See `backend/src/services/contextCommentService.js` for the authoritative
 * validation/authorization rules.
 */

export type ContextCommentKind = "principle" | "task";

export type ContextCommentOwnerScope = "me" | "global" | "mixed";

export interface ContextCommentSearchResponse {
  data: {
    own: Comment[];
    global: Comment[];
  };
  meta: {
    kind: ContextCommentKind;
    ownerScope: ContextCommentOwnerScope;
  };
}

export interface AttachedContextComment extends Comment {
  link_id?: string | null;
  link_properties?: Record<string, unknown> | null;
  linked_at?: string | null;
  link_sort_order?: number | null;
}

export interface ContextCommentListResponse {
  items: AttachedContextComment[];
}

export interface CreateContextCommentPayload {
  /** Required when creating a brand-new principle/task. */
  content?: string;
  /** Optional owner-side privacy; defaults to 'public'. */
  privacy?: PrivacyLevel;
  /** Free-form properties forwarded to the comment row. */
  properties?: Record<string, unknown>;
  /** Optional ISO timestamp. For tasks this is the due date. */
  timeline_ts?: string | null;
  /** Optional numeric place id for geo-scoped context (rare). */
  place_id?: number | null;
  /**
   * When set, link the caller's EXISTING owner-anchored comment instead
   * of creating a new one. The backend verifies ownership + kind.
   */
  existing_comment_id?: number;
}

export interface CreateContextCommentResponse {
  comment?: Comment;
  comment_id?: number;
  link_id: string | null;
}

/**
 * Map a kind to the nested API registry entry (no string duplication).
 */
const routesForKind = (kind: ContextCommentKind): typeof apiRoutes.posts.principles => {
  if (kind === "principle") {
    return apiRoutes.posts.principles;
  }
  return apiRoutes.posts.tasks;
};

export const contextCommentApi = {
  /** List attached principles / tasks for a host post. */
  listForPost: async (
    postId: number,
    kind: ContextCommentKind,
    params?: { limit?: number; offset?: number }
  ): Promise<ContextCommentListResponse> => {
    const response = await api.get<ContextCommentListResponse>(
      routesForKind(kind).list(postId),
      { params }
    );
    return response.data;
  },

  /**
   * Create a brand-new principle/task AND link it, or link an existing
   * one. The picker uses both shapes depending on user action.
   */
  createOrLink: async (
    postId: number,
    kind: ContextCommentKind,
    payload: CreateContextCommentPayload
  ): Promise<CreateContextCommentResponse> => {
    const response = await api.post<CreateContextCommentResponse>(
      routesForKind(kind).create(postId),
      payload
    );
    return response.data;
  },

  /** Remove the `entity_rel` link between the post and the comment. */
  unlink: async (
    postId: number,
    kind: ContextCommentKind,
    commentId: number
  ): Promise<{ detached: boolean }> => {
    const response = await api.delete<{ detached: boolean }>(
      routesForKind(kind).unlink(postId, commentId)
    );
    return response.data;
  },

  /** Rewrite sort_order for all active links in the sequence given. */
  reorder: async (
    postId: number,
    kind: ContextCommentKind,
    order: number[]
  ): Promise<{ count: number }> => {
    const response = await api.patch<{ count: number }>(
      routesForKind(kind).reorder(postId),
      { order }
    );
    return response.data;
  },

  /**
   * Search owner-anchored principle/task comments for the picker. The
   * backend returns results split into `own` (caller-authored) and
   * `global` (everyone else, audience-filtered). Callers pass
   * `ownerScope='me'` to search only their own rows, `'global'` for only
   * others, or `'mixed'` for both.
   *
   * Pagination is global-branch only: when `offset > 0`, the server
   * returns `own: []` and the next page of `global` rows. The picker
   * uses this to infinite-scroll public/anonymous rows without
   * duplicating the caller's own rows on every page.
   *
   * `audiences` restricts results to rows whose `properties.privacy` is
   * in the provided list. Anonymous host forms pass
   * `['public','anonymous']` so personal/private rows are not offered.
   */
  search: async (params: {
    kind: ContextCommentKind;
    q?: string;
    ownerScope?: ContextCommentOwnerScope;
    limit?: number;
    offset?: number;
    audiences?: string[];
  }): Promise<ContextCommentSearchResponse> => {
    const { audiences, ...rest } = params;
    const response = await api.get<ContextCommentSearchResponse>(
      apiRoutes.comments.search,
      {
        params: {
          ...rest,
          audiences: audiences && audiences.length > 0 ? audiences.join(",") : undefined,
        },
      }
    );
    return response.data;
  },
};
